import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useDataContext } from "../../../utils/DataContext";
import { isVisibleTrainingModelType } from "../../../constant/featureVisibility";
import { buttonText } from "../../../utils/buttonText";
import { notificationText } from "../../../utils/notificationText";
import { pageText } from "../../../utils/pageText";


function OrganizationTrainDefaultsPage({ organizationName }) {
  const { userToken } = useDataContext();
  const [models, setModels] = useState({});
  const [modelType, setModelType] = useState("");
  const [editorValue, setEditorValue] = useState("{}");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const modelTypes = useMemo(() => Object.keys(models), [models]);
  const selected = models[modelType];

  const loadDefaults = async (preferredModel) => {
    if (!organizationName || !userToken) return;
    try {
      const response = await axios.get("/auth/organization/train-defaults", {
        params: {
          requesterToken: userToken,
          organization: organizationName,
        },
      });
      const nextModels = Object.fromEntries(
        Object.entries(response.data || {}).filter(([name]) =>
          isVisibleTrainingModelType(name)
        )
      );
      const nextType =
        (preferredModel && nextModels[preferredModel] && preferredModel) ||
        Object.keys(nextModels)[0] ||
        "";
      setModels(nextModels);
      setModelType(nextType);
      setEditorValue(
        JSON.stringify(nextModels[nextType]?.effective_defaults || {}, null, 2)
      );
      setError("");
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.response?.data ||
          requestError.message
      );
    }
  };

  useEffect(() => {
    loadDefaults();
  }, [organizationName, userToken]);

  const selectModel = (nextType) => {
    setModelType(nextType);
    setEditorValue(
      JSON.stringify(models[nextType]?.effective_defaults || {}, null, 2)
    );
    setError("");
  };

  const parseEditor = () => {
    const parsed = JSON.parse(editorValue);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      throw new Error("train_json must be a JSON object.");
    }
    return parsed;
  };

  const saveDefaults = async () => {
    try {
      const trainJson = parseEditor();
      setSaving(true);
      setError("");
      await axios.post("/auth/organization/train-defaults/set", {
        requesterToken: userToken,
        organization: organizationName,
        model_type: modelType,
        train_json: trainJson,
      });
      await loadDefaults(modelType);
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          saveError.response?.data ||
          saveError.message
      );
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(`Reset ${modelType} to code defaults?`)) return;
    try {
      setSaving(true);
      setError("");
      await axios.post("/auth/organization/train-defaults/delete", {
        requesterToken: userToken,
        organization: organizationName,
        model_type: modelType,
      });
      await loadDefaults(modelType);
    } catch (resetError) {
      setError(
        resetError.response?.data?.error ||
          resetError.response?.data ||
          resetError.message
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="org-train-defaults">
      <div className="org-card">
        <div className="org-card-header">
          <div>
            <h2 className="org-card-title">{pageText("Training defaults")}</h2>
            <p className="org-card-subtitle">
              {pageText("Configure persistent defaults for each model in")} {organizationName}.{" "}
              {pageText("Users may override values for one run, but cannot edit this saved configuration.")}
            </p>
          </div>
          <span className="org-count-badge">
            {selected?.organization_defaults &&
            Object.keys(selected.organization_defaults).length
              ? pageText("Organization override")
              : pageText("Code defaults")}
          </span>
        </div>

        <div className="org-card-body">
          <label className="org-field">
            <span className="org-field-label">{pageText("Model")}</span>
            <select
              className="org-field-input"
              value={modelType}
              onChange={(event) => selectModel(event.target.value)}
            >
              {modelTypes.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="org-field org-train-json-field">
            <span className="org-field-label">train_json</span>
            <textarea
              className="org-train-json-editor"
              value={editorValue}
              onChange={(event) => setEditorValue(event.target.value)}
              spellCheck="false"
              aria-label={`${modelType} train_json`}
            />
          </label>

          {error ? (
            <div className="org-train-error">{notificationText(error)}</div>
          ) : null}
        </div>

        <div className="org-card-footer">
          <span className="org-footer-note">
            {pageText("Merge order: code defaults → organization defaults → run input")}
          </span>
          <div className="org-footer-actions">
            <button
              type="button"
              className="org-btn org-btn-secondary"
              onClick={resetDefaults}
              disabled={saving || !modelType}
            >
              {buttonText("Reset")}
            </button>
            <button
              type="button"
              className="org-btn org-btn-primary"
              onClick={saveDefaults}
              disabled={saving || !modelType}
            >
              {buttonText(saving ? "Saving…" : "Save defaults")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default OrganizationTrainDefaultsPage;
